import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { EventDetailsColumnType, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { getAnimationIconForSport, getStatsIcon, getVideoIcon } from '@frontend/sports/common/core/feature/icon';
import { ThemeOptionsWithDefault } from '@frontend/sports/common/ui/shared';
import { GridMedia } from '@frontend/sports/grid/core/feature/model';
import { MediaEventSource } from '@frontend/sports/grid/media/feature/model';
import { MediaApiService } from '@frontend/sports/grid/media/feature/state';
import { TrackingVideoSource } from '@frontend/sports/tracking/feature';

import { BetRadarStatisticsService } from '../../bet-radar/bet-radar-statistics.service';
import { HeaderNavigationService } from '../../event-details-common/header-navigation/header-navigation.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { ShowcaseService } from '../showcase.service';
import { ShowcaseTrackingService } from '../showcase.tracking.service';

@Component({
    selector: 'ms-showcase-media',
    templateUrl: 'showcase-media.component.html',
    styleUrls: ['./showcase-media.component.scss'],
    standalone: false,
})
export class ShowcaseMediaComponent implements OnChanges {
    private eventState = new Map<string, string>();
    hasStats: boolean;
    hasVideo: boolean;
    hasAnimation: boolean;
    tabs: TabBarItem[];
    feature: TabBarItem;
    event: EventModel;
    media: GridMedia;
    mediaType = EventDetailsColumnType;
    stats: string;
    video: string;
    animation: string;
    isShowcaseModule: boolean;
    currentEventId = 'eventId';

    @HostBinding()
    get class(): string {
        const firstClass = this.getHostFirstClass();

        return [firstClass].join(' ');
    }

    readonly segmentedControlThemeInverse: ThemeOptionsWithDefault<boolean> = {
        'bwin-dark': false,
        'giocodigitale': false,
        'premium-dark': false,
        'sports-interaction': false,
        'sportingbet-2': false,
        'mgm-4': false,
        'default': true,
    };

    @Input() showHeaderContent: boolean;
    @Input() tabSelectedEvent: EventModel;
    @Output() videoPinToggle = new EventEmitter<boolean>();
    @Output() featureChange = new EventEmitter<number>();
    @Output() heightChange = new EventEmitter<number>();
    @Input() showAlertScoreboard = false;

    constructor(
        private helper: EventMediaHelperService,
        private headerNavigation: HeaderNavigationService,
        private betRadarStatistics: BetRadarStatisticsService,
        private showcaseTrackingService: ShowcaseTrackingService,
        private showcaseService: ShowcaseService,
        private mediaApi: MediaApiService,
    ) {
        this.showcaseService.selectedAlertEvent.pipe(takeUntilDestroyed()).subscribe(() => (this.showAlertScoreboard = true));
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (
            (changes.tabSelectedEvent && changes.tabSelectedEvent.firstChange) ||
            (this.tabSelectedEvent && this.tabSelectedEvent.id !== this.eventState.get(this.currentEventId)) ||
            (this.showAlertScoreboard && this.showHeaderContent)
        ) {
            await this.checkMediaControls(this.tabSelectedEvent);
            this.showAlertScoreboard = false; // To avoid it to become true whenever any push update, which is no use after scoreboard tab is already active.
        }
    }

    private getHostFirstClass(): string {
        return this.showHeaderContent ? 'segmented-control-showcase-media-mobile' : 'showcase-media-desktop';
    }

    private async checkMediaControls(event: EventModel): Promise<void> {
        this.hasStats = this.helper.hasStats(event);
        this.hasVideo = this.helper.hasVideo(event);
        this.hasAnimation = this.helper.hasAnimationOrSimulation(event);
        this.stats = getStatsIcon(false);
        this.video = getVideoIcon(false);
        this.animation = getAnimationIconForSport(event.sport.id, false);
        if (this.showHeaderContent) {
            this.tabs = await this.headerNavigation.init(event, true);
        }
        this.eventState.set(this.currentEventId, event.id);
    }

    tabSelectedmedia(event: string): void {
        const id = parseInt(event);
        const selectedTab = this.tabs.find((tab) => tab.id === id);
        this.trackTabSelectedMedia(id);
        this.headerNavigation.updateSelected(selectedTab);
    }

    activateMedia(event: MouseEvent, activeType: EventDetailsColumnType): void {
        const isActive =
            activeType === EventDetailsColumnType.Stats ||
            activeType === EventDetailsColumnType.Video ||
            activeType === EventDetailsColumnType.Animation;

        if (isActive) {
            this.selectMediaStyles(activeType);
            this.setEventId(this.tabSelectedEvent.id, activeType);
            if (activeType === EventDetailsColumnType.Stats) {
                if (this.tabSelectedEvent.statsId) {
                    this.betRadarStatistics.open(this.tabSelectedEvent.statsId);
                }
            }
        }
        this.trackActivateMedia(activeType);
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    private selectMediaStyles(activeType: EventDetailsColumnType): void {
        this.stats = getStatsIcon(activeType === EventDetailsColumnType.Stats);
        this.video = getVideoIcon(activeType === EventDetailsColumnType.Video);
        this.animation = getAnimationIconForSport(this.tabSelectedEvent.sport.id, activeType === EventDetailsColumnType.Animation);
    }

    private trackActivateMedia(activeType: EventDetailsColumnType): void {
        this.showcaseTrackingService.trackTabsInteractionForMedia(activeType, this.tabSelectedEvent);
    }

    private trackTabSelectedMedia(event: number): void {
        this.showcaseTrackingService.trackTabsInteractionForHeader(event, this.tabSelectedEvent);
    }

    private setEventId(eventId: string, tab: EventDetailsColumnType): void {
        this.mediaApi.setEventId(eventId, MediaEventSource.Grid, tab, TrackingVideoSource.ShowcaseModule);
    }
}
